
import { db, auth } from './firebase';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, collection, query, where, getDocs, orderBy, runTransaction, limit } from 'firebase/firestore';
import { SavedTrip, Collaborator } from '../types';

/**
 * Add a collaborator to a trip.
 * Updates both the collaborators array and the denormalized collaboratorEmails array.
 */
export const addCollaborator = async (
  tripId: string,
  email: string,
  role: 'editor' | 'viewer',
  addedBy: string
): Promise<void> => {
  if (!db) throw new Error('Database not available');

  try {
    const normalizedEmail = email.toLowerCase().trim();
    const tripRef = doc(db, 'itineraries', tripId);

    await runTransaction(db, async (transaction) => {
      const tripDoc = await transaction.get(tripRef);
      if (!tripDoc.exists()) throw new Error('Trip not found');

      const tripData = tripDoc.data();
      const existing = (tripData.collaborators || []) as Collaborator[];
      if (existing.some(c => c.email === normalizedEmail)) {
        throw new Error('This person is already a collaborator');
      }

      // Also prevent adding the owner
      if (tripData.userId === addedBy) {
        // Owner is adding someone — that's fine
      }

      // Check if the email matches the owner
      if (tripData.ownerEmail === normalizedEmail) {
        throw new Error('Cannot add the trip owner as a collaborator');
      }

      const collaborator: Collaborator = {
        email: normalizedEmail,
        role,
        addedAt: new Date().toISOString(),
        addedBy,
        status: 'pending',
      };

      transaction.update(tripRef, {
        collaborators: [...existing, collaborator],
        collaboratorEmails: [...(tripData.collaboratorEmails || []), normalizedEmail],
      });
    });
  } catch (error) {
    if (error instanceof Error) throw new Error(`Failed to add collaborator: ${error.message}`);
    throw new Error('Failed to add collaborator: unknown error');
  }
};

/**
 * Remove a collaborator from a trip.
 */
export const removeCollaborator = async (tripId: string, email: string): Promise<void> => {
  if (!db) throw new Error('Database not available');

  try {
    const normalizedEmail = email.toLowerCase().trim();
    const tripRef = doc(db, 'itineraries', tripId);

    const tripSnap = await getDoc(tripRef);
    if (!tripSnap.exists()) throw new Error('Trip not found');

    const tripData = tripSnap.data();
    const existing = (tripData.collaborators || []) as Collaborator[];
    const collaborator = existing.find(c => c.email === normalizedEmail);

    if (!collaborator) throw new Error('Collaborator not found');

    // Remove from both arrays
    await updateDoc(tripRef, {
      collaborators: arrayRemove(collaborator),
      collaboratorEmails: arrayRemove(normalizedEmail),
    });
  } catch (error) {
    if (error instanceof Error) throw new Error(`Failed to remove collaborator: ${error.message}`);
    throw new Error('Failed to remove collaborator: unknown error');
  }
};

/**
 * Update a collaborator's role.
 * Uses a transaction to atomically update the collaborator entry.
 */
export const updateCollaboratorRole = async (
  tripId: string,
  email: string,
  role: 'editor' | 'viewer'
): Promise<void> => {
  if (!db) throw new Error('Database not available');

  try {
    const normalizedEmail = email.toLowerCase().trim();
    const tripRef = doc(db, 'itineraries', tripId);

    await runTransaction(db, async (transaction) => {
      const tripDoc = await transaction.get(tripRef);
      if (!tripDoc.exists()) throw new Error('Trip not found');

      const data = tripDoc.data();
      const existing = (data.collaborators || []) as Collaborator[];
      const oldCollaborator = existing.find(c => c.email === normalizedEmail);

      if (!oldCollaborator) throw new Error('Collaborator not found');

      const collaborators = existing.map((c: Collaborator) =>
        c.email === normalizedEmail ? { ...c, role } : c
      );

      transaction.update(tripRef, { collaborators });
    });
  } catch (error) {
    if (error instanceof Error) throw new Error(`Failed to update collaborator role: ${error.message}`);
    throw new Error('Failed to update collaborator role: unknown error');
  }
};

/**
 * Get trips shared with a specific user email.
 * Queries itineraries where collaboratorEmails contains the given email.
 */
export const getSharedTrips = async (userEmail: string): Promise<SavedTrip[]> => {
  if (!db) return [];

  try {
    const normalizedEmail = userEmail.toLowerCase().trim();

    const q = query(
      collection(db, 'itineraries'),
      where('collaboratorEmails', 'array-contains', normalizedEmail),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    const trips: SavedTrip[] = [];

    snapshot.forEach((docSnap) => {
      trips.push({ id: docSnap.id, ...docSnap.data() } as SavedTrip);
    });

    return trips;
  } catch (error) {
    if (error instanceof Error) throw new Error(`Failed to get shared trips: ${error.message}`);
    throw new Error('Failed to get shared trips: unknown error');
  }
};

/**
 * Check if a user can edit a trip.
 * Returns true if the user is the owner or an editor collaborator.
 */
export const canEditTrip = (trip: any, userEmail: string, userId: string): boolean => {
  // Owner can always edit
  if (trip.userId === userId) return true;

  // Check collaborator role
  const normalizedEmail = userEmail.toLowerCase().trim();
  const collab = (trip.collaborators || []).find(
    (c: Collaborator) => c.email === normalizedEmail
  );
  return collab?.role === 'editor';
};

/**
 * Check if a user is a collaborator on a trip (any role).
 */
export const isCollaborator = (trip: any, userEmail: string): boolean => {
  if (!trip.collaboratorEmails || !userEmail) return false;
  const normalizedEmail = userEmail.toLowerCase().trim();
  return trip.collaboratorEmails.includes(normalizedEmail);
};

/**
 * Get the collaborator object for a user on a trip.
 */
export const getCollaboratorInfo = (trip: any, userEmail: string): Collaborator | undefined => {
  if (!trip.collaborators || !userEmail) return undefined;
  const normalizedEmail = userEmail.toLowerCase().trim();
  return (trip.collaborators as Collaborator[]).find(c => c.email === normalizedEmail);
};

/**
 * Accept a collaboration invitation.
 * Uses a transaction to atomically update the collaborator's status from 'pending' to 'accepted'.
 */
export const acceptCollaboration = async (tripId: string, userEmail: string): Promise<void> => {
  if (!db) throw new Error('Database not available');

  try {
    const normalizedEmail = userEmail.toLowerCase().trim();
    const tripRef = doc(db, 'itineraries', tripId);

    await runTransaction(db, async (transaction) => {
      const tripDoc = await transaction.get(tripRef);
      if (!tripDoc.exists()) throw new Error('Trip not found');

      const data = tripDoc.data();
      const existing = (data.collaborators || []) as Collaborator[];
      const oldCollaborator = existing.find(c => c.email === normalizedEmail);

      if (!oldCollaborator) throw new Error('You are not a collaborator on this trip');
      if (oldCollaborator.status === 'accepted') return; // Already accepted

      const collaborators = existing.map((c: Collaborator) =>
        c.email === normalizedEmail ? { ...c, status: 'accepted' as const } : c
      );

      transaction.update(tripRef, { collaborators });
    });
  } catch (error) {
    if (error instanceof Error) throw new Error(`Failed to accept collaboration: ${error.message}`);
    throw new Error('Failed to accept collaboration: unknown error');
  }
};
